"use client";

import { useState } from "react";
import dropdownData from "../content/dropdown_content.json";
import langData from "../content/languages.json";
import { MultiSelect } from "react-multi-select-component";

export default function PercentageChangeForm() {
  const [languages, setLanguages] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [startYear, setStartYear] = useState("");
  const [endYear, setEndYear] = useState("");
  const [comparisonMetric, setComparisonMetric] = useState("");
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(false); // NEW: loading state

  // Static language options from languages.json
  const languageOptions = Object.entries(langData).map(([code, lang]) => ({
    label: `${lang.localname} (${lang.name})`,
    value: code,
  }));

  // Static project types from dropdown_content.json
  const projectTypeOptions = dropdownData.projectTypes.map((type) => ({
    label: type.label,
    value: type.value,
  }));

  const comparisonMetricOptions = dropdownData.comparisonMetrics.map(metric => ({
    label: metric.label,
    value: metric.value
  }));

  const handleSubmit = async e => {
    e.preventDefault();

    if (!languages.length || !projectTypes.length || !startYear || !endYear || !comparisonMetric) {
      alert("Please fill in all the fields!");
      return;
    }

    setLoading(true); // NEW: Set loading state to true before API call

    const selectedLanguages = languages.map(lang => lang.value);
    const selectedProjectTypes = projectTypes.map(type => type.value);
    const startDate = `${startYear}0101`;
    const endDate = `${endYear}0101`;

    const requestBody = {
      projects: selectedLanguages,
      project_types: selectedProjectTypes,
      start_date: startDate,
      end_date: endDate,
      comparison_metric: comparisonMetric,
    };

    console.log("Sending Request:", requestBody);

    try {
      const response = await fetch("/percentage_change/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error ${response.status}: ${errorData.detail || response.statusText}`);
      }

      const data = await response.json();
      if (!data || data.length === 0) {
        alert("No data found for the selected filters. Try different inputs.");
        return;
      }
      setResultData(data);
      console.log("API Response:", data);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert(error.message || "An error occurred while fetching data.");
    } finally {
      setLoading(false); // NEW: Reset loading state after API call (both success and error)
    }
  };

  return (
    <section style={{ maxWidth: "800px", margin: "auto", padding: "20px" }}>
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: "15px" }}>
        <div style={{ display: "grid", gap: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Wiki Languages</label>
          <MultiSelect
            options={languageOptions}
            value={languages}
            onChange={setLanguages}
            labelledBy="Select languages"
          />
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Project Types</label>
          <MultiSelect
            options={projectTypeOptions}
            value={projectTypes}
            onChange={setProjectTypes}
            labelledBy="Select project types"
          />
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          <label style={{ fontWeight: "bold" }}>Comparison Metric</label>
          <select
            value={comparisonMetric}
            onChange={e => setComparisonMetric(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid gray"
            }}
          >
            <option value="">Select a metric</option>
            {comparisonMetricOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div style={{ display: "grid", gap: "10px" }}>
            <label style={{ fontWeight: "bold" }}>Start Year</label>
            <input
              type="number"
              placeholder="YYYY"
              min="2000"
              max={new Date().getFullYear()}
              value={startYear}
              onChange={e => setStartYear(e.target.value)}
              style={{ width: "95%", padding: "10px", borderRadius: "5px", border: "1px solid gray", marginRight:"5px" }}
            />
          </div>
          <div style={{ display: "grid", gap: "10px" }}>
            <label style={{ fontWeight: "bold" }}>End Year</label>
            <input
              type="number"
              placeholder="YYYY"
              min="2000"
              max={new Date().getFullYear()}
              value={endYear}
              onChange={e => setEndYear(e.target.value)}
              style={{ width: "95%", padding: "10px", borderRadius: "5px", border: "1px solid gray" }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading} // NEW: Disable button when loading
          style={{
            backgroundColor: loading ? "#999" : "#007BFF", // NEW: Change color when loading
            color: "white",
            padding: "10px",
            borderRadius: "5px",
            border: "none",
            cursor: loading ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Loading..." : "Submit"} {/* NEW: Change text when loading */}
        </button>
      </form>

      {resultData && (
        <div style={{ marginTop: "20px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>Results</h2>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Metric</th>
                <th style={tableHeaderStyle}>Language</th>
                <th style={tableHeaderStyle}>Project Type</th>
                <th style={tableHeaderStyle}>% Change</th>
              </tr>
            </thead>
            <tbody>
              {resultData.map(({ metric, language, project_type, value }, index) => (
                <tr key={index}>
                  <td style={tableCellStyle}>{metric}</td>
                  <td style={tableCellStyle}>{(langData[language] && langData[language].localname) || "NA"}</td>
                  <td style={tableCellStyle}>{project_type || "NA"}</td>
                  <td style={tableCellStyle}>{value !== undefined ? value : "NA"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Table styling
const tableHeaderStyle = {
  border: "1px solid gray",
  padding: "10px",
  textAlign: "left",
  backgroundColor: "#f8f8f8"
};

const tableCellStyle = {
  border: "1px solid gray",
  padding: "10px"
};
